import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Clock, Pause, Percent } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatMinutesAsTime } from '@/utils/analyticsHelpers';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { fromZonedTime } from 'date-fns-tz';
// DTC-K: route the pause-overview "now" anchor through the canonical
// clock so analytics duration computations are deterministic under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
// DMC-D3c-1: canonical deleted-state helper.
import { isTicketDeleted } from '@/utils/ticketDeletedState';

interface PauseTimeOverviewProps {
  shopId: string;
  startDate: string;
  endDate: string;
  shopTimezone: string;
}

interface ReasonBreakdown {
  reason: string;
  totalMinutes: number;
  count: number;
  percentage: number;
}

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--primary))',
];

// Hex equivalents for background tints (HSL variables don't support hex opacity suffix)
const HEX_COLORS = [
  '#8884d8', // chart-1
  '#82ca9d', // chart-2
  '#ffc658', // chart-3
  '#ff7300', // chart-4
  '#0088fe', // chart-5
  '#00C49F', // primary
];

export function PauseTimeOverview({ shopId, startDate, endDate, shopTimezone }: PauseTimeOverviewProps) {
  const [loading, setLoading] = useState(true);
  const [totalPauseMinutes, setTotalPauseMinutes] = useState(0);
  const [avgPausePerTicket, setAvgPausePerTicket] = useState(0);
  const [pausePercentage, setPausePercentage] = useState(0);
  const [reasonBreakdown, setReasonBreakdown] = useState<ReasonBreakdown[]>([]);

  useEffect(() => {
    loadPauseStats();
    // KPI-R70-FIX (MED-1): include shopTimezone so a shop switch refetches
    // with the correct TZ bounds. Previously the effect was keyed only on
    // [shopId, startDate, endDate]; since `fromZonedTime(...startDate..., shopTimezone)`
    // silently uses the stale TZ on re-render, the query could return rows
    // from the wrong 24-hour window until a date manually changed.
  }, [shopId, startDate, endDate, shopTimezone]);

  async function loadPauseStats() {
    setLoading(true);
    try {
      const startUtc = fromZonedTime(`${startDate}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${endDate}T23:59:59`, shopTimezone).toISOString();

      // Get pause sessions in date range - include sessions without duration (still active)
      const { data: pauseSessions } = await supabase
        .from('ticket_pause_sessions')
        .select('id, ticket_id, pause_reason, duration_minutes, paused_at, resumed_at')
        .eq('shop_id', shopId)
        .gte('paused_at', startUtc)
        .lte('paused_at', endUtc);

      if (!pauseSessions?.length) {
        setTotalPauseMinutes(0);
        setAvgPausePerTicket(0);
        setPausePercentage(0);
        setReasonBreakdown([]);
        setLoading(false);
        return;
      }

      // Get ticket parking_lot + status so we can match the filter rules
      // already enforced by `useMinuteProductivityMetrics` (the primary
      // consumer of pause minutes on the technician productivity card).
      //
      // KPI-R70-FIX (HIGH-1): previously this view filtered open pauses by
      // parking_lot only but still included open pauses on Completed /
      // Deleted tickets — e.g. a pause left dangling when a ticket was
      // completed via a different code path would be counted here with
      // live-growing elapsed minutes, producing a total that diverges
      // from the productivity card's number for the same date range.
      // Same KPI, two different totals — the classic trust break. Mirror
      // the productivity-card rule so both views report the same pause
      // total for the same inputs.
      const ticketIds = [...new Set(pauseSessions.map(s => s.ticket_id))];
      // DMC-D3c-1: project `deleted_at` alongside status so the
      // canonical helper can apply both-rep defense-in-depth.
      const { data: tickets } = await supabase
        .from('tickets')
        .select('id, parking_lot, status, deleted_at')
        .in('id', ticketIds);

      const ticketInfoMap = new Map(
        tickets?.map(t => [
          t.id,
          { parking_lot: t.parking_lot, status: t.status, deleted_at: t.deleted_at },
        ]) || []
      );

      // Calculate duration for each session (use actual or calculate from paused_at)
      const now = clock.now();
      const sessionsWithDuration = pauseSessions
        .filter(s => {
          const info = ticketInfoMap.get(s.ticket_id);
          // Completed / Deleted tickets: only count CLOSED pauses. Open
          // pauses on terminal tickets are noise (never truly resumed).
          // DMC-D3c-1: deletion check via canonical helper (both reps).
          if (info?.status === 'Completed' || isTicketDeleted(info ?? {})) {
            return s.resumed_at !== null;
          }
          // Non-terminal tickets: closed pauses always count.
          if (s.resumed_at !== null) return true;
          // Open pauses: exclude if ticket is currently in parking lot.
          return info?.parking_lot !== true;
        })
        .map(s => {
          let duration = s.duration_minutes;
          if (duration === null && s.paused_at) {
            // Calculate live duration for still-paused sessions (only reached
            // here for non-terminal, non-parking-lot tickets).
            const pausedAt = new Date(s.paused_at);
            duration = Math.round((now.getTime() - pausedAt.getTime()) / 60000);
          }
          return { ...s, calculatedDuration: duration || 0 };
        });

      // Calculate totals
      const totalMinutes = sessionsWithDuration.reduce((sum, s) => sum + s.calculatedDuration, 0);
      const uniqueTickets = new Set(sessionsWithDuration.map(s => s.ticket_id)).size;
      const avgPerTicket = uniqueTickets > 0 ? Math.round(totalMinutes / uniqueTickets) : 0;

      // Get total workflow time (actual_minutes from completed tickets)
      const { data: completedTickets } = await supabase
        .from('tickets')
        .select('actual_minutes')
        .eq('shop_id', shopId)
        .eq('status', 'Completed')
        // CSI-S1: exclude soft-deleted Completed tickets from pause-time analytics.
        .is('deleted_at', null)
        .gte('completed_at', startUtc)
        .lte('completed_at', endUtc);

      const totalWorkflowMinutes = completedTickets?.reduce((sum, t) => sum + (t.actual_minutes || 0), 0) || 0;
      const pausePct = totalWorkflowMinutes > 0 ? Math.round((totalMinutes / (totalWorkflowMinutes + totalMinutes)) * 100) : 0;

      // Aggregate by reason
      const reasonMap = new Map<string, { minutes: number; count: number }>();
      sessionsWithDuration.forEach(s => {
        const reason = s.pause_reason || 'Unknown';
        const existing = reasonMap.get(reason) || { minutes: 0, count: 0 };
        existing.minutes += s.calculatedDuration;
        existing.count += 1;
        reasonMap.set(reason, existing);
      });

      const breakdown: ReasonBreakdown[] = Array.from(reasonMap.entries())
        .map(([reason, data]) => ({
          reason,
          totalMinutes: data.minutes,
          count: data.count,
          percentage: totalMinutes > 0 ? Math.round((data.minutes / totalMinutes) * 100) : 0,
        }))
        .sort((a, b) => b.totalMinutes - a.totalMinutes || a.reason.localeCompare(b.reason));

      setTotalPauseMinutes(totalMinutes);
      setAvgPausePerTicket(avgPerTicket);
      setPausePercentage(pausePct);
      setReasonBreakdown(breakdown);
    } catch (error) {
      console.error('Error loading pause stats:', error);
    } finally {
      setLoading(false);
    }
  }

  const chartData = reasonBreakdown.map(r => ({
    name: r.reason,
    value: r.totalMinutes,
  }));

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pause-Time Overview</CardTitle>
        <CardDescription>
          Total time lost to pauses and breakdown by reason
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <Clock className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
            <p className="text-2xl font-bold">{formatMinutesAsTime(totalPauseMinutes)}</p>
            <p className="text-xs text-muted-foreground">Total Paused Time</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <Pause className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
            <p className="text-2xl font-bold">{formatMinutesAsTime(avgPausePerTicket)}</p>
            <p className="text-xs text-muted-foreground">Avg Per Ticket</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <Percent className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
            <p className="text-2xl font-bold">{pausePercentage}%</p>
            <p className="text-xs text-muted-foreground">Of Workflow</p>
          </div>
        </div>

        {reasonBreakdown.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No pause data in this date range</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="55%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {chartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={HEX_COLORS[index % HEX_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatMinutesAsTime(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Reason List */}
            <div className="space-y-3">
              {reasonBreakdown.map((r, index) => (
                <div 
                  key={r.reason} 
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ 
                    backgroundColor: `${HEX_COLORS[index % HEX_COLORS.length]}15`,
                    borderLeft: `4px solid ${HEX_COLORS[index % HEX_COLORS.length]}` 
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <div>
                      <p className="font-medium text-sm">{r.reason}</p>
                      <p className="text-xs text-muted-foreground">{r.count} pauses</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatMinutesAsTime(r.totalMinutes)}</p>
                    <p className="text-xs text-muted-foreground">{r.percentage}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
